import React from 'react';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { I18n } from '@aws-amplify/core';
import { Form, Modal } from 'antd';

import { DeleteBaseRequest, HierarchyEnum, UpdateBaseRequest } from '../hooks/types';
import { RiskDescriptionSelect } from '@/components/ui/Inputs/RiskDescriptionSelect';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useDelete, useGetRiskDescriptions, useUpdate } from '../hooks';
import { RiskDescriptionDTO } from '@/components/ui/Inputs';
import { DescriptionManager } from './DescriptionManager';
import { useManageCategoryContext } from '../context';

const { useFormInstance, useWatch } = Form;

interface ManageRiskDescriptionProps {
	stepKeyId: string;
}

export const ManageRiskDescription: React.FC<ManageRiskDescriptionProps> = ({ stepKeyId }) => {
	const form = useFormInstance();
	const { company, organization } = useApplicationContext();
	const { isEditing, categoryId, handleIsEditing, treatFieldValidation } = useManageCategoryContext();

	const riskDescriptionId = useWatch(['risk_description_id'], form);

	const {
		data: riskDescriptions,
		isLoading: gettingRiskDescriptions,
		isFetching: fetchingRiskDescriptions,
		isError: errorGettingRiskDescriptions
	} = useGetRiskDescriptions({
		company_id: company?.id,
		risk_category_id: categoryId,
		organization_id: organization?.id
	});
	const { mutateAsync: deleteData } = useDelete<DeleteBaseRequest, RiskDescriptionDTO>();
	const { mutateAsync: updateData } = useUpdate<UpdateBaseRequest, RiskDescriptionDTO>();

	const currentName = riskDescriptions?.find(({ id }) => id === riskDescriptionId)?.description;

	async function handleUpdateName() {
		try {
			const values = form.getFieldValue(['editing']);
			if (values) {
				const body: UpdateBaseRequest = {
					id: values?.currentId,
					company_id: company?.id,
					description: values.risk_description_name,
					organization_id: organization?.id,
					hierarchy_field: HierarchyEnum.riskDescription
				};
				const updated = await updateData(body);
				if (updated.id) {
					handleIsEditing(false);
					form.setFieldValue('risk_description_id', updated.id);
					form.setFieldValue(['editing', 'risk_description_name'], undefined);
				}
			}
		} catch (error: any) {
			treatFieldValidation(error);
		}
	}

	async function handleDeleteDescription() {
		Modal.confirm({
			title: I18n.get('Heads up!'),
			content: I18n.get('Want to delete this description?'),
			icon: <ExclamationCircleOutlined />,
			okText: I18n.get('Confirm'),
			cancelText: I18n.get('Cancel'),
			okType: 'danger',
			onOk: async () => {
				const body: DeleteBaseRequest = {
					id: riskDescriptionId,
					company_id: company?.id,
					organization_id: organization?.id,
					hierarchy_field: HierarchyEnum.riskDescription
				};
				const updated = await deleteData(body);
				if (updated.id) {
					handleClearDescription();
					handleIsEditing(false);
					form.setFieldValue(['editing', 'risk_description_name'], undefined);
				}
			}
		});
	}

	function handleDescriptionSelect(id: string) {
		form.setFieldValue(['risk_description_id'], id);
		form.setFieldValue(['risk_damage_id'], undefined);
		form.setFieldValue(['severity_id'], undefined);
		form.setFieldValue(['current_severity_id'], undefined);
	}

	function handleClearDescription() {
		form.setFieldValue(['risk_description_id'], undefined);
		form.setFieldValue(['risk_damage_id'], undefined);
		form.setFieldValue(['severity_id'], undefined);
		form.setFieldValue(['current_severity_id'], undefined);
	}

	return (
		<DescriptionManager
			key={categoryId}
			parentId={categoryId}
			currentName={currentName}
			currentId={riskDescriptionId}
			onUpdateName={handleUpdateName}
			fieldLabel={'a risk description'}
			onDelete={handleDeleteDescription}
			fieldName={['risk_description_id']}
			editingNameField={['editing', 'risk_description_name']}
		>
			<RiskDescriptionSelect
				size="middle"
				allowCreation={true}
				stepKeyId={stepKeyId}
				riskCategoryId={categoryId}
				fieldName={['risk_description_id']}
				riskDescriptions={riskDescriptions}
				riskDescriptionId={riskDescriptionId}
				loading={isEditing || fetchingRiskDescriptions}
				handleClearRiskDescription={handleClearDescription}
				handleSelectRiskDescription={handleDescriptionSelect}
				disabled={
					errorGettingRiskDescriptions || gettingRiskDescriptions || fetchingRiskDescriptions || isEditing
				}
			/>
		</DescriptionManager>
	);
};
